import type { AiChatConversation, AiChatMessage } from "@/api/ai-chat";
import type { ConversationKind } from "@/ai-chat/sseClient";

import { PREVIEW_ENV_ID } from "../runtime";
import {
  blocksToStoredContent,
  getResponseForPrompt,
  kindHidesFirstUserMessage,
} from "./responses";

// Conversations the user creates in preview mode live here for the lifetime
// of the page load. Reload starts fresh — same contract as the synthetic
// graph, which rebuilds from seed each load (see graph/index.ts).
export type StoredMessage = {
  id: string;
  ordinal: number;
  role: "user" | "assistant";
  // Anthropic-shaped content blocks: text → { type: "text", text }, structured
  // emit_<kind> tool calls → { type: "tool_use", name, input }. Matches what
  // extractAssistantBlocks / extractUserText in useConversationStream.ts read.
  content: { blocks: Array<Record<string, unknown>> };
  createdAt: string;
  // Mirrors the production CROChatMessage.VisibleToUser flag. The first user
  // prompt of an app-generated conversation (weekly_summary, funnel_dig_deeper)
  // is persisted with visibleToUser=false; the simulator/REST surface filters
  // those out before the chat UI sees them. Treated as `true` when undefined.
  visibleToUser?: boolean;
};

export type StoredConversation = {
  id: string;
  title: string;
  kind: ConversationKind;
  status: "active" | "deleted";
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  messages: StoredMessage[];
};

export const PREVIEW_UID = "preview-uid-00000000";

const conversations = new Map<string, StoredConversation>();
let seeded = false;

function isoDaysAgo(days: number): string {
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

// Three seeded conversations so the sidebar isn't empty on first visit and
// each of the major narrative threads (Web Analytics, Funnels, Session
// Replay) shows at least one example. The weekly-summary and funnel
// drop-off bodies are deliberately short — the simulator's full canned
// responses are richer. The session-replay seed (CE-1733) reuses the full
// canned response so opening it from the sidebar lands on a populated
// cards view, matching what a fresh prompt of the same question produces.
function seedHistorical(): void {
  const oneDayAgo = isoDaysAgo(1);
  const threeDaysAgo = isoDaysAgo(3);
  const twoDaysAgo = isoDaysAgo(2);

  conversations.set("preview-conv-weekly", {
    id: "preview-conv-weekly",
    title: "Weekly Summary: example",
    kind: "weekly_summary",
    status: "active",
    createdAt: threeDaysAgo,
    updatedAt: threeDaysAgo,
    lastMessageAt: threeDaysAgo,
    messages: [
      {
        id: "preview-msg-weekly-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [
            {
              type: "text",
              text: "Generate the weekly analytics summary for my account.",
            },
          ],
        },
        createdAt: threeDaysAgo,
        visibleToUser: !kindHidesFirstUserMessage("weekly_summary"),
      },
      {
        id: "preview-msg-weekly-asst",
        ordinal: 1,
        role: "assistant",
        content: {
          blocks: [
            {
              type: "text",
              text: "Last week the appointment-booking funnel converted 4.2% of sessions, with the largest drop-off at the insurance verification step. Mobile traffic underperformed desktop by 18%.",
            },
          ],
        },
        createdAt: threeDaysAgo,
      },
    ],
  });

  conversations.set("preview-conv-dropoff", {
    id: "preview-conv-dropoff",
    title: "Why is there a large drop-off in the appointment booking funne...",
    kind: "standard",
    status: "active",
    createdAt: oneDayAgo,
    updatedAt: oneDayAgo,
    lastMessageAt: oneDayAgo,
    messages: [
      {
        id: "preview-msg-dropoff-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [
            {
              type: "text",
              text: "Why is there a large drop-off in the appointment booking funnel?",
            },
          ],
        },
        createdAt: oneDayAgo,
      },
      {
        id: "preview-msg-dropoff-asst",
        ordinal: 1,
        role: "assistant",
        content: {
          blocks: [
            {
              type: "text",
              text: "The biggest leak is between Insurance Verification and Confirmation — only 38% of users who enter insurance details continue to confirm. The fields most likely to cause abandonment are member-ID and group-number.",
            },
          ],
        },
        createdAt: oneDayAgo,
      },
    ],
  });

  const sessionReplayPrompt =
    "Show me recordings of people who dropped off at insurance verification.";
  const sessionReplayBlocks = getResponseForPrompt({
    prompt: sessionReplayPrompt,
    conversationKind: "standard",
  });
  conversations.set("preview-conv-session-replay", {
    id: "preview-conv-session-replay",
    title:
      "Show me recordings of people who dropped off at insurance verifica...",
    kind: "standard",
    status: "active",
    createdAt: twoDaysAgo,
    updatedAt: twoDaysAgo,
    lastMessageAt: twoDaysAgo,
    messages: [
      {
        id: "preview-msg-session-replay-user",
        ordinal: 0,
        role: "user",
        content: {
          blocks: [{ type: "text", text: sessionReplayPrompt }],
        },
        createdAt: twoDaysAgo,
      },
      {
        id: "preview-msg-session-replay-asst",
        ordinal: 1,
        role: "assistant",
        content: { blocks: blocksToStoredContent(sessionReplayBlocks) },
        createdAt: twoDaysAgo,
      },
    ],
  });
}

function ensureSeeded(): void {
  if (seeded) return;
  seedHistorical();
  seeded = true;
}

export function listConversations(): AiChatConversation[] {
  ensureSeeded();
  return Array.from(conversations.values())
    .filter((c) => c.status !== "deleted")
    .map(toApiConversation);
}

export function getConversation(id: string): {
  conversation: AiChatConversation;
  messages: AiChatMessage[];
} | null {
  ensureSeeded();
  const conv = conversations.get(id);
  if (!conv || conv.status === "deleted") return null;
  // Mirror the production REST surface, which runs FilterMessagesForUser on
  // the way out so callers never see hidden prompts.
  return {
    conversation: toApiConversation(conv),
    messages: conv.messages
      .filter((m) => m.visibleToUser !== false)
      .map((m) => toApiMessage(conv.id, m)),
  };
}

export function upsertConversation(input: {
  id: string;
  title: string;
  status: string;
  kind?: ConversationKind;
}): void {
  ensureSeeded();
  const existing = conversations.get(input.id);
  const now = new Date().toISOString();
  if (existing) {
    existing.title = input.title;
    existing.status = input.status === "deleted" ? "deleted" : "active";
    existing.updatedAt = now;
    if (input.kind) existing.kind = input.kind;
    return;
  }
  conversations.set(input.id, {
    id: input.id,
    title: input.title,
    kind: input.kind ?? "standard",
    status: input.status === "deleted" ? "deleted" : "active",
    createdAt: now,
    updatedAt: now,
    lastMessageAt: null,
    messages: [],
  });
}

export function appendMessage(
  conversationId: string,
  msg: Omit<StoredMessage, "createdAt">,
): StoredMessage | null {
  ensureSeeded();
  const conv = conversations.get(conversationId);
  if (!conv) return null;
  const stored: StoredMessage = { ...msg, createdAt: new Date().toISOString() };
  conv.messages.push(stored);
  conv.lastMessageAt = stored.createdAt;
  conv.updatedAt = stored.createdAt;
  return stored;
}

export function nextOrdinal(conversationId: string): number {
  const conv = conversations.get(conversationId);
  if (!conv || conv.messages.length === 0) return 0;
  return conv.messages[conv.messages.length - 1].ordinal + 1;
}

export function getStoredConversation(id: string): StoredConversation | null {
  ensureSeeded();
  return conversations.get(id) ?? null;
}

export function __resetPreviewAiChatStore(): void {
  conversations.clear();
  seeded = false;
}

function toApiConversation(conv: StoredConversation): AiChatConversation {
  return {
    id: conv.id,
    env_id: PREVIEW_ENV_ID,
    uid: PREVIEW_UID,
    title: conv.title,
    kind: conv.kind,
    status: conv.status,
    processing_status: "idle",
    processing_started_at: null,
    last_message_at: conv.lastMessageAt,
    created_at: conv.createdAt,
    updated_at: conv.updatedAt,
  };
}

function toApiMessage(
  conversationId: string,
  msg: StoredMessage,
): AiChatMessage {
  return {
    id: msg.id,
    env_id: PREVIEW_ENV_ID,
    uid: PREVIEW_UID,
    conversation_id: conversationId,
    parent_message_id: null,
    role: msg.role,
    content: msg.content,
    model_config: {},
    usage: {},
    status: "final",
    visible_to_user: msg.visibleToUser !== false,
    created_at: msg.createdAt,
  };
}
